import type { AppRouter } from "@mapcraft-tasks/api";
import type { PullResult, QueuedOp, Transport } from "@mapcraft-tasks/client-store";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import superjson from "superjson";

let onUnauthorized: (() => void) | null = null;

/**
 * Ставится из `lib/session`: обратный импорт завёл бы цикл, колбэк оставляет
 * зависимость односторонней.
 */
export function setUnauthorizedHandler(handler: () => void): void {
	onUnauthorized = handler;
}

/**
 * Единственный файл приложения, знающий про сеть. Экраны сюда не импортируются —
 * их контракт кончается на хуках стора (offline-sync §3).
 *
 * Адрес бэкенда фронту не нужен: всё за одним nginx, пути относительные.
 */
export const trpc = createTRPCClient<AppRouter>({
	links: [
		httpBatchLink({
			url: "/api/trpc",
			transformer: superjson,
			// 401 ловится ЗДЕСЬ, а не по `TRPCClientError.data.httpStatus`: его отдаёт
			// мидлварь Hono своим телом, не tRPC-шейпом ошибки, — `data` будет пустым.
			fetch: async (url, options) => {
				const res = await fetch(url, { ...options, credentials: "same-origin" });
				// 401 — это НЕ «нет связи»: очередь толкать некуда, пока человек не вошёл.
				// Ни очередь, ни реплику при этом не трогаем — они переживут вход.
				if (res.status === 401) {
					onUnauthorized?.();
				}
				return res;
			}
		})
	]
});

/**
 * `Entities` сервера собран из строк Drizzle, клиентский — из структурных типов
 * `packages/domain`. Формы совпадают поле в поле (superjson довозит `Date` живыми),
 * но общего номинального типа у них нет и быть не должно: иначе в браузерный бандл
 * поехал бы `packages/db`. Приведение — ровно здесь, в одном месте на всё приложение.
 */
export const transport: Transport = {
	pull: async (cursor) => (await trpc.sync.pull.mutate({ cursor: cursor ?? undefined })) as unknown as PullResult,

	push: async (ops: QueuedOp[]) =>
		trpc.sync.push.mutate({
			ops: ops.map((op) => ({ opId: op.opId, seq: op.seq, name: op.name, input: op.input }))
		}),

	settings: async () => ({ tz: (await trpc.settings.get.query()).tz })
};

// --- авторизация -----------------------------------------------------------

export type Session = { authenticated: boolean; tz?: string };

/**
 * `null` — «сервер не ответил, про сессию не известно ничего». Это НЕ «не авторизован»:
 * ручка публичная и 401 не отдаёт, поэтому и `!res.ok`, и упавший `fetch` значат одно —
 * сервера сейчас нет. Разница видна человеку: «не вошли» это форма входа, а «не знаем» —
 * оффлайн-работа с локальной репликой.
 */
export async function currentSession(): Promise<Session | null> {
	try {
		const res = await fetch("/api/auth/me", { credentials: "same-origin" });
		if (!res.ok) {
			return null;
		}
		return (await res.json()) as Session;
	} catch {
		return null;
	}
}

/** На голом `fetch`: её 401 — «неверный пароль», а не «сессия истекла». */
export async function login(password: string): Promise<boolean> {
	const res = await fetch("/api/auth/login", {
		method: "POST",
		headers: { "content-type": "application/json" },
		credentials: "same-origin",
		body: JSON.stringify({ password })
	});
	return res.ok;
}

export async function logout(): Promise<void> {
	await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" });
}

/**
 * Поток свежести. Никаких данных в нём нет — только «пора за дельтой»; поэтому
 * переподключение после обрыва ничего не теряет: `pull` всё равно идёт от курсора.
 *
 * `EventSource` сам переподключается с backoff — своего ретрая не пишем.
 */
export function syncEvents(onSync: () => void): () => void {
	const source = new EventSource("/api/events", { withCredentials: true });
	source.addEventListener("sync", () => onSync());
	return () => source.close();
}
